const LS_ACCESS = 'fh_app_access_token'
const LS_REFRESH = 'fh_app_refresh_token'
const LS_EXP_MS = 'fh_app_access_exp_ms'

export function apiBaseUrl(): string {
  return process.env.NEXT_PUBLIC_API_URL ?? 'https://api.fairhelm.com'
}

export function clearAppAuthSession(): void {
  if (typeof window === 'undefined') return
  localStorage.removeItem(LS_ACCESS)
  localStorage.removeItem(LS_REFRESH)
  localStorage.removeItem(LS_EXP_MS)
}

export function getAppRefreshToken(): string | null {
  if (typeof window === 'undefined') return null
  return localStorage.getItem(LS_REFRESH)
}

export function saveAppAuthSession(data: {
  access_token: string
  refresh_token: string
  expires_in: number
}): void {
  if (typeof window === 'undefined') return
  localStorage.setItem(LS_ACCESS, data.access_token)
  localStorage.setItem(LS_REFRESH, data.refresh_token)
  localStorage.setItem(LS_EXP_MS, String(Date.now() + data.expires_in * 1000))
}

/** Seconds since Unix epoch when access token expires (best-effort from local clock). */
export function appAccessTokenExpiresAtSec(): number | null {
  if (typeof window === 'undefined') return null
  const raw = localStorage.getItem(LS_EXP_MS)
  if (!raw) return null
  const ms = Number(raw)
  if (!Number.isFinite(ms)) return null
  return Math.floor(ms / 1000)
}

export async function refreshAppAuthSession(): Promise<{
  access_token: string
  refresh_token: string
  expires_in: number
} | null> {
  if (typeof window === 'undefined') return null
  const rt = localStorage.getItem(LS_REFRESH)
  if (!rt) return null
  const res = await fetch(`${apiBaseUrl()}/api/auth/v1/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh_token: rt }),
  })
  if (!res.ok) {
    clearAppAuthSession()
    return null
  }
  const data = (await res.json()) as {
    access_token: string
    refresh_token: string
    expires_in: number
  }
  saveAppAuthSession(data)
  return data
}

const EXPIRY_BUFFER_SEC = 120

export async function getFreshAppAccessToken(
  options?: { forceRefresh?: boolean },
): Promise<string | null> {
  if (typeof window === 'undefined') return null
  if (options?.forceRefresh) {
    const d = await refreshAppAuthSession()
    return d?.access_token ?? null
  }
  const expMsRaw = localStorage.getItem(LS_EXP_MS)
  const access = localStorage.getItem(LS_ACCESS)
  const expMs = expMsRaw ? Number(expMsRaw) : 0
  const now = Date.now()
  if (access && expMs > now + EXPIRY_BUFFER_SEC * 1000) {
    return access
  }
  const d = await refreshAppAuthSession()
  return d?.access_token ?? null
}
